// ============================================================
// GICS Sector Classification
// ============================================================

export type GicsSector =
  | 'Energy'
  | 'Materials'
  | 'Industrials'
  | 'Consumer Discretionary'
  | 'Consumer Staples'
  | 'Health Care'
  | 'Financials'
  | 'Information Technology'
  | 'Communication Services'
  | 'Utilities'
  | 'Real Estate'
  | 'Unknown';

export type SectorClassification = 'growth' | 'defensive' | 'cyclical' | 'mature';

export const SECTOR_CLASSIFICATIONS: Record<GicsSector, SectorClassification> = {
  'Information Technology': 'growth',
  'Communication Services': 'growth',
  'Health Care': 'growth',
  'Consumer Discretionary': 'cyclical',
  'Industrials': 'cyclical',
  'Materials': 'cyclical',
  'Energy': 'cyclical',
  'Financials': 'cyclical',
  'Consumer Staples': 'defensive',
  'Utilities': 'defensive',
  'Real Estate': 'mature',
  'Unknown': 'mature',
};

// ============================================================
// Raw Financial Statement Data (from APIs)
// ============================================================

export interface AnnualIncomeStatement {
  fiscalYear: number;        // e.g. 2023
  revenue: number;           // Total Revenue
  grossProfit: number;       // Gross Profit
  operatingIncome: number;   // Operating Income / EBIT
  netIncome: number;         // Net Income
  eps: number;               // Earnings Per Share (diluted)
  sharesOutstanding: number; // Diluted shares
}

export interface AnnualBalanceSheet {
  fiscalYear: number;
  currentAssets: number;
  currentLiabilities: number;
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  longTermDebt: number;
  cash: number;
}

export interface AnnualCashFlow {
  fiscalYear: number;
  operatingCashFlow: number;
  capitalExpenditures: number; // negative values expected from some APIs
  freeCashFlow: number;        // operatingCashFlow + capex (or pre-calculated)
  dividendsPaid: number;
}

export interface StockOverview {
  ticker: string;
  name: string;
  exchange: string;
  sector: GicsSector;
  industry: string;
  marketCap: number;
  peRatio: number | null;
  pbRatio: number | null;
  dividendYield: number | null;
  beta: number | null;
  description: string;
  country: string;
  currency: string;
}

export interface PriceQuote {
  ticker: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  marketCap: number;
  timestamp: number; // unix ms
}

// ============================================================
// Calculated Metrics
// ============================================================

export interface AnnualMetrics {
  fiscalYear: number;
  // Income
  revenue: number;
  revenueGrowth: number | null;         // YoY %
  grossProfit: number;
  grossProfitGrowth: number | null;     // YoY %
  grossMargin: number | null;           // %
  netIncome: number;
  profitMargin: number | null;          // % net income / revenue
  operatingMargin: number | null;
  eps: number;
  epsGrowth: number | null;             // YoY %
  // Balance Sheet
  currentRatio: number | null;
  debtToEquity: number | null;
  debtToAssets: number | null;
  // Cash Flow
  freeCashFlow: number;
  fcfMargin: number | null;             // FCF / Revenue %
  fcfGrowth: number | null;             // YoY %
}

// ============================================================
// Evaluation / Report
// ============================================================

export type EvaluationStatus = 'PASS' | 'FAIL' | 'WATCH' | 'PENDING';

export interface EvaluationCriteria {
  label: string;
  value: number | null;
  target: string;
  passed: boolean | null;
  description: string;
}

export interface SectorAnalysis {
  gicsSector: GicsSector;
  gicsCode: string;
  classification: SectorClassification;
  industry: string;
  moatIndicators: string[];           // e.g. ["High margins vs peers", "Brand recognition"]
  competitiveNotes: string;
}

export interface EvaluationReport {
  id: string;                          // UUID
  ticker: string;
  companyName: string;
  exchange: string;
  evaluationDate: number;              // unix timestamp
  analysisPeriod: {
    startYear: number;
    endYear: number;
  };
  annualMetrics: AnnualMetrics[];      // sorted ascending by fiscalYear
  overview: StockOverview | null;
  sectorAnalysis: SectorAnalysis;
  // Scoring
  criteria: EvaluationCriteria[];
  overallStatus: EvaluationStatus;
  score: number;                       // 0–100
  // User-added content
  comments: string;                    // Markdown
  tags: string[];
  // Data source
  dataSource: 'alpha_vantage' | 'fmp' | 'csv' | 'mock';
  // Sync state
  nostrEventId?: string;
  syncedAt?: number;
}

// ============================================================
// Watchlist
// ============================================================

export interface WatchlistItem {
  ticker: string;
  companyName: string;
  exchange: string;
  addedAt: number;                     // unix timestamp
  lastEvaluationId?: string;
  lastEvaluationDate?: number;
  lastEvaluationStatus?: EvaluationStatus;
  notes?: string;
  alertPrice?: number;
}

// ============================================================
// Settings / API Config
// ============================================================

export type ApiProvider = 'alpha_vantage' | 'fmp' | 'none';

export interface ApiSettings {
  provider: ApiProvider;
  alphaVantageKey: string;
  fmpKey: string;
}

export interface AppSettings {
  apiSettings: ApiSettings;
  defaultLookbackYears: number;        // 5 or 10
  defaultProfitMarginTarget: number;   // default 15
  defaultCurrentRatioTarget: number;   // default 1.0
  benchmarkSymbol: string;             // 'SPY', 'QQQ', 'DIA'
}

export const DEFAULT_APP_SETTINGS: AppSettings = {
  apiSettings: {
    provider: 'none',
    alphaVantageKey: '',
    fmpKey: '',
  },
  defaultLookbackYears: 10,
  defaultProfitMarginTarget: 15,
  defaultCurrentRatioTarget: 1.0,
  benchmarkSymbol: 'SPY',
};

// ============================================================
// CSV Import
// ============================================================

export interface CsvImportData {
  incomeStatements: AnnualIncomeStatement[];
  balanceSheets: AnnualBalanceSheet[];
  cashFlows: AnnualCashFlow[];
}

// ============================================================
// API Cache
// ============================================================

export interface CacheEntry<T> {
  data: T;
  fetchedAt: number; // unix timestamp
  expiresAt: number; // unix timestamp
}
